import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PhoneCall, ShieldCheck, Trash2, Loader2, MessageSquare, RefreshCcw, Ban, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTwilio() {
  const [numbers, setNumbers] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState("+260");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastCode, setLastCode] = useState(null);
  const [optOuts, setOptOuts] = useState([]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [{ data }, opt] = await Promise.all([
        axios.get(`${API}/admin/twilio/verified-numbers`),
        axios.get(`${API}/admin/sms/opt-outs`).catch(() => ({ data: { items: [] } })),
      ]);
      setNumbers(data.numbers || []);
      setMeta({
        from_number: data.from_number, account_sid: data.account_sid,
        count: data.count, live_error: data.live_error, console_url: data.console_url,
      });
      setOptOuts(opt.data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load Twilio numbers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const requestVerification = async (e) => {
    e.preventDefault();
    if (!phone.startsWith("+") || phone.length < 10) return toast.error("Phone must be E.164 (e.g. +260971234567)");
    setBusy(true); setLastCode(null);
    try {
      const { data } = await axios.post(`${API}/admin/twilio/verify-number`, {
        phone_number: phone, friendly_name: name || undefined,
      });
      setLastCode(data);
      if (data.mode === "api") {
        toast.success(`Calling ${phone} — tell them to enter code ${data.validation_code}`);
      } else {
        toast.info("Twilio trial blocks programmatic verification. Use the Twilio Console link shown below.");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification request failed");
    } finally { setBusy(false); }
  };

  const removeNumber = async (n) => {
    if (!window.confirm(`Remove ${n.phone_number} (${n.friendly_name || "unnamed"}) from Twilio verified list?`)) return;
    try {
      await axios.delete(`${API}/admin/twilio/verified-numbers/${n.sid}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-10 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
            <MessageSquare className="h-3.5 w-3.5" /> Admin · Twilio
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Verified numbers</h1>
          <p className="mt-1 text-sm text-slate-600">
            While your Twilio account is on trial, SMS can only reach numbers verified here. Once you upgrade, this becomes optional.
          </p>
        </div>
        <button onClick={load} className="btn-outline text-sm" data-testid="twilio-refresh-btn">
          <RefreshCcw className="mr-1 inline h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="mt-6 grid gap-4 rounded-3xl bg-white p-6 text-sm card-shadow md:grid-cols-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Twilio SID</div>
          <div className="mt-1 truncate font-mono text-slate-800">{meta.account_sid || "—"}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Sender</div>
          <div className="mt-1 font-mono text-slate-800">{meta.from_number || "—"}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Verified count</div>
          <div className="mt-1 font-mono text-slate-800">{meta.count ?? "—"}</div>
        </div>
      </div>

      {meta.live_error && (
        <div className="mt-4 rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="twilio-live-warning">
          <div className="font-semibold">Twilio API list is blocked on this trial account.</div>
          <div className="mt-1">Verifications must be completed in the Twilio Console. We track your local ledger below and any verifications you complete in the Console will be reflected here once your account is upgraded.</div>
          {meta.console_url && (
            <a href={meta.console_url} target="_blank" rel="noreferrer" className="mt-2 inline-block font-semibold text-copper hover:underline">
              Open Twilio Verified Caller IDs →
            </a>
          )}
        </div>
      )}

      {/* Verify form */}
      <form onSubmit={requestVerification} className="mt-6 rounded-3xl bg-white p-6 card-shadow" data-testid="verify-number-form">
        <h2 className="font-display text-lg font-bold text-slate-900">Verify a Zambian number</h2>
        <p className="mt-1 text-xs text-slate-500">
          Twilio will call the number. Tell the recipient to answer and enter the code we show you here.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Phone (E.164)</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value.trim())}
              placeholder="+260971234567"
              className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-sm outline-none focus:border-copper focus:bg-white"
              data-testid="verify-phone-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Friendly name (optional)</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Guest at Lakeview"
              className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
              data-testid="verify-name-input"
            />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="verify-submit-btn">
            {busy ? <Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> : <PhoneCall className="mr-1 inline h-4 w-4" />}
            {busy ? "Calling…" : "Call & verify"}
          </button>
          {lastCode && (
            <div className="rounded-2xl border border-copper/30 bg-copper/5 px-4 py-2 text-sm" data-testid="verify-last-code">
              <span className="text-slate-600">Enter code </span>
              <span className="font-mono text-lg font-bold text-copper">{lastCode.validation_code}</span>
              <span className="text-slate-600"> on the call to {lastCode.phone_number}</span>
            </div>
          )}
        </div>
      </form>

      {/* Existing verified numbers */}
      <div className="mt-8">
        <h2 className="font-display text-xl font-bold text-slate-900">Currently verified</h2>
        {loading ? (
          <div className="mt-4 rounded-3xl bg-white p-10 text-center text-slate-500">Loading…</div>
        ) : error ? (
          <div className="mt-4 rounded-3xl border border-red-200 bg-red-50 p-6 text-sm text-red-700" data-testid="twilio-error">
            {error}
          </div>
        ) : numbers.length === 0 ? (
          <div className="mt-4 rounded-3xl bg-white p-10 text-center text-slate-500" data-testid="twilio-empty">
            No numbers verified yet. Verify one above to unlock SMS to that number.
          </div>
        ) : (
          <div className="mt-4 space-y-3" data-testid="twilio-list">
            {numbers.map((n) => (
              <div key={n.sid} data-testid={`twilio-${n.sid}`} className="flex items-center justify-between rounded-3xl bg-white p-4 card-shadow">
                <div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-forest" />
                    <span className="font-mono text-sm font-semibold text-slate-900">{n.phone_number}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{n.friendly_name || "—"} · added {n.date_created ? new Date(n.date_created).toLocaleDateString() : "—"}</div>
                </div>
                <button onClick={() => removeNumber(n)} className="btn-outline text-xs !py-2" data-testid={`twilio-remove-${n.sid}`}>
                  <Trash2 className="mr-1 inline h-3.5 w-3.5" /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* SMS Opt-outs */}
      <div className="mt-10">
        <h2 className="font-display text-xl font-bold text-slate-900">SMS opt-outs</h2>
        <p className="mt-1 text-sm text-slate-500">
          Guests who replied <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">STOP</code> are silenced automatically. They can text
          <code className="mx-1 rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">START</code> to opt back in.
        </p>
        <div className="mt-3 rounded-3xl border border-slate-200 bg-white p-5 text-xs text-slate-600 card-shadow">
          <div className="flex items-start gap-2">
            <MessageSquare className="mt-0.5 h-4 w-4 text-forest" />
            <div>
              <div className="font-semibold text-slate-900">Twilio webhook URL</div>
              <div>Configure this URL on your Twilio number under Messaging → &ldquo;A MESSAGE COMES IN&rdquo;:</div>
              <code className="mt-1 block truncate rounded bg-slate-50 px-2 py-1 font-mono text-xs">{`${process.env.REACT_APP_BACKEND_URL}/api/twilio/webhook/incoming-sms`}</code>
              <a
                href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming"
                target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 font-semibold text-copper hover:underline"
              >
                Open Twilio number settings <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
        {optOuts.length === 0 ? (
          <div className="mt-3 rounded-3xl bg-white p-6 text-center text-sm text-slate-500" data-testid="opt-outs-empty">
            No opt-outs yet.
          </div>
        ) : (
          <div className="mt-3 space-y-2" data-testid="opt-outs-list">
            {optOuts.map((o) => (
              <div key={o.phone_number} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-white p-4 card-shadow">
                <div className="flex items-center gap-3">
                  <Ban className={`h-4 w-4 ${o.opted_out ? "text-red-500" : "text-slate-400"}`} />
                  <span className="font-mono text-sm font-semibold text-slate-900">{o.phone_number}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${o.opted_out ? "bg-red-100 text-red-700" : "bg-forest/10 text-forest"}`}>
                    {o.opted_out ? "Opted out" : "Opted back in"}
                  </span>
                </div>
                <div className="text-xs text-slate-500">
                  Via <span className="font-mono">{o.keyword}</span> · {o.at ? new Date(o.at).toLocaleString() : "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
